import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Plus,
  Search,
  Eye,
  Phone,
  Mail,
  MessageCircle,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  MapPin,
  X,
  User,
  Wrench,
  Globe,
  ExternalLink,
  FileText
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { format, isToday, isThisWeek, isThisMonth } from 'date-fns'
import { PremiumTable } from '@/components/enhanced/PremiumTable'
import { formatRelativeDate } from '@/utils/displayFormat'
import { formatPhoneNumber } from '@/features/integrations/utils/integrationUtils'

// Database types based on existing schema
interface Lead {
  id: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  service_street?: string;
  message?: string;
  service_requested?: string;
  service_slug?: string;
  location?: string;
  submission_url?: string;
  form_id?: string;
  form_name?: string;
  submission_status?: string;
  status: 'new' | 'contacted' | 'qualified' | 'quoted' | 'won' | 'lost';
  file_uploads?: any;
  created_at: string;
  updated_at: string;
}

// Database operations
const fetchLeads = async (): Promise<Lead[]> => {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

const updateLeadStatus = async (id: string, status: string): Promise<void> => {
  const { error } = await supabase
    .from('leads')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
};

// Date range filter type
type DateRangeFilter = 'all' | 'today' | 'thisWeek' | 'thisMonth' | 'last7days' | 'last30days'

export default function LeadsManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'contacted' | 'qualified' | 'quoted' | 'won' | 'lost'>('all')
  const [dateFilter, setDateFilter] = useState<DateRangeFilter>('all')
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)

  const queryClient = useQueryClient();

  // Helper to truncate text
  const truncateText = (text: string | undefined, maxLength: number) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  // Check if date is within filter range
  const isWithinDateFilter = (dateStr: string, filter: DateRangeFilter): boolean => {
    if (filter === 'all') return true
    const date = new Date(dateStr)
    const now = new Date()

    switch (filter) {
      case 'today':
        return isToday(date)
      case 'thisWeek':
        return isThisWeek(date, { weekStartsOn: 1 })
      case 'thisMonth':
        return isThisMonth(date)
      case 'last7days': {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        return date >= sevenDaysAgo
      }
      case 'last30days': {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        return date >= thirtyDaysAgo
      }
      default:
        return true
    }
  }


  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/)
    const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('')
    return initials || 'U'
  }

  const formatPhone = (phone?: string) => {
    if (!phone) return ''
    return formatPhoneNumber(phone)
  }

  const safeHostname = (url?: string) => {
    if (!url) return null
    try {
      if (/^https?:\/\//i.test(url)) return new URL(url).hostname
      if (typeof window !== 'undefined' && window.location?.origin) return new URL(url, window.location.origin).hostname
      return null
    } catch {
      return null
    }
  }

  const parseLeadMessage = (message?: string) => {
    if (!message) return null

    const segments = message.split(/\n|\|/).map(part => part.trim()).filter(Boolean)
    const parsed = {
      reference: undefined as string | undefined,
      appointment: {} as { date?: string; time?: string; window?: string },
      service: undefined as string | undefined,
      location: undefined as string | undefined,
      availability: undefined as string | undefined,
      extras: [] as Array<{ label: string; value: string }>,
      notes: [] as string[]
    }

    segments.forEach(segment => {
      const [rawKey, ...rest] = segment.split(':')
      if (rest.length) {
        const key = rawKey.trim().toLowerCase()
        const value = rest.join(':').trim()

        switch (key) {
          case 'reference':
            parsed.reference = value
            break
          case 'appointment date':
            parsed.appointment.date = value
            break
          case 'appointment time':
            parsed.appointment.time = value
            break
          case 'appointment':
            parsed.appointment.window = value
            break
          case 'service':
          case 'service requested':
          case 'requested service':
            parsed.service = value
            break
          case 'address':
          case 'location':
            parsed.location = value
            break
          case 'availability':
            parsed.availability = value
            break
          default:
            parsed.extras.push({ label: rawKey.trim(), value })
        }
      } else {
        parsed.notes.push(segment)
      }
    })

    return parsed
  }

  // Fetch leads from database
  const { data: leads = [], isLoading, error } = useQuery({
    queryKey: ['leads'],
    queryFn: fetchLeads
  });

  // Update lead status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateLeadStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      // Search across name, email, phone, and message
      const searchLower = searchTerm.toLowerCase()
      const matchesSearch = !searchTerm ||
        lead.client_name.toLowerCase().includes(searchLower) ||
        lead.client_email.toLowerCase().includes(searchLower) ||
        lead.client_phone?.toLowerCase().includes(searchLower) ||
        lead.message?.toLowerCase().includes(searchLower) ||
        lead.service_requested?.toLowerCase().includes(searchLower)
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter
      const matchesDate = isWithinDateFilter(lead.created_at, dateFilter)
      return matchesSearch && matchesStatus && matchesDate
    })
  }, [leads, searchTerm, statusFilter, dateFilter])

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || dateFilter !== 'all' || searchTerm !== ''

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setDateFilter('all')
  }

  const getStatusColor = (status: Lead['status']) => {
    switch (status) {
      case 'new': return 'bg-info/100 text-white font-semibold'
      case 'contacted': return 'bg-warning/100 text-white font-semibold'
      case 'qualified': return 'bg-primary/100 text-white font-semibold'
      case 'quoted': return 'bg-accent/100 text-white font-semibold'
      case 'won': return 'bg-success/100 text-white font-semibold'
      case 'lost': return 'bg-destructive/100 text-white font-semibold'
      default: return 'bg-muted text-foreground'
    }
  }

  const handleStatusChange = (id: string, newStatus: Lead['status']) => {
    updateStatusMutation.mutate({ id, status: newStatus });
  }

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsViewDialogOpen(true);
  }

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    won: leads.filter(l => l.status === 'won').length,
    conversionRate: leads.length > 0 ? Math.round((leads.filter(l => l.status === 'won').length / leads.length) * 100) : 0
  }

  const parsedMessage = useMemo(() => parseLeadMessage(selectedLead?.message), [selectedLead?.message])

  const columns = useMemo(() => [
    {
      header: "Contact",
      className: "w-[280px]",
      cell: (lead: Lead) => (
        <TooltipProvider>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm">
              <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold text-sm">
                {getInitials(lead.client_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 space-y-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="font-semibold text-foreground truncate leading-tight cursor-help">
                    {lead.client_name}
                  </div>
                </TooltipTrigger>
                <TooltipContent>{lead.client_name}</TooltipContent>
              </Tooltip>
              <div className="flex items-center flex-wrap gap-2 mt-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={`mailto:${lead.client_email}`}
                      className="text-xs text-muted-foreground hover:text-primary truncate max-w-[160px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.client_email}
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>{lead.client_email}</TooltipContent>
                </Tooltip>
                {lead.client_phone && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={`tel:${lead.client_phone}`}
                        className="text-xs text-muted-foreground hover:text-primary whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatPhone(lead.client_phone)}
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>{formatPhone(lead.client_phone)}</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-[11px] font-mono cursor-help">
                      #{lead.id.toString().slice(-6)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Lead ID: {lead.id}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </TooltipProvider>
      )
    },
    {
      header: "Request",
      className: "w-[200px]",
      cell: (lead: Lead) => (
        <div className="space-y-1.5">
          {lead.service_requested ? (
            <span className="text-sm font-medium text-foreground">
              {lead.service_requested}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">General Inquiry</span>
          )}
          {lead.message && !lead.message.includes('Reference:') && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-xs text-muted-foreground line-clamp-1 cursor-help">
                    {truncateText(lead.message, 40)}
                  </p>
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px]">
                  <p className="whitespace-pre-wrap text-sm">{lead.message}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )
    },
    {
      header: "Status",
      className: "w-[100px]",
      cell: (lead: Lead) => (
        <Badge className={`${getStatusColor(lead.status)} shadow-sm font-medium`}>
          {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
        </Badge>
      )
    },
    {
      header: "Source",
      className: "w-[140px]",
      cell: (lead: Lead) => {
        const hostname = safeHostname(lead.submission_url || '');
        return hostname ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs font-normal cursor-help">
                  {hostname}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">{lead.form_name || 'Contact Form'}</p>
                <p className="text-xs text-muted-foreground mt-1">{lead.submission_url}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-sm text-muted-foreground">
            {lead.form_name || 'Direct'}
          </span>
        );
      }
    },
    {
      header: "Date",
      className: "w-[120px]",
      cell: (lead: Lead) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {(() => {
                const dateParts = formatRelativeDate(lead.created_at)
                return (
                  <div className="text-sm">
                    {dateParts.relative && (
                      <div className="font-medium text-foreground">
                        {dateParts.relative}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {dateParts.absolute}
                    </div>
                  </div>
                )
              })()}
            </TooltipTrigger>
            <TooltipContent>
              {format(new Date(lead.created_at), 'EEEE, MMMM d, yyyy \'at\' h:mm a')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
    {
      header: "",
      className: "w-[80px]",
      cell: (lead: Lead) => (
        <div className="flex items-center justify-end gap-0.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                  onClick={(e) => { e.stopPropagation(); handleViewLead(lead); }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View details</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                  asChild
                >
                  <a href={`mailto:${lead.client_email}`} onClick={(e) => e.stopPropagation()}>
                    <Mail className="h-4 w-4" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Send email</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )
    }
  ], [getInitials, formatPhone, truncateText, getStatusColor, safeHostname, handleViewLead])

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Leads</h2>
          <p className="text-muted-foreground">Unable to load leads data. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leads Management</h1>
          <p className="text-muted-foreground">
            Track and manage customer inquiries and sales opportunities.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" className="min-h-[44px] px-4 text-muted-foreground hover:text-primary hover:bg-primary/5">
            <Calendar className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Schedule Follow-up</span>
            <span className="sm:hidden">Schedule</span>
          </Button>
          <Button className="min-h-[44px] px-4 shadow-sm">
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Add Lead</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All customer inquiries (all time)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Leads</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.new}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting contact
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Won Leads</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.won}</div>
            <p className="text-xs text-muted-foreground">
              Successful conversions (all time)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.conversionRate > 0 ? `${stats.conversionRate}%` : 'No conversions yet'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.conversionRate > 0 ? 'Lead to customer' : 'Once you win a lead, the rate appears here.'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Pipeline</CardTitle>
          <CardDescription>
            Manage customer inquiries and track sales opportunities.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col sm:flex-row flex-1 gap-3">
              <div className="relative flex-1 max-w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 min-h-[44px]"
                />
              </div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as any)}>
                <SelectTrigger className="w-full sm:w-[150px] min-h-[44px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              <Select value={dateFilter} onValueChange={(value) => setDateFilter(value as DateRangeFilter)}>
                <SelectTrigger className="w-full sm:w-[150px] min-h-[44px]">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="last30days">Last 30 Days</SelectItem>
                  <SelectItem value="thisWeek">This Week</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 mt-2 sm:mt-0">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-h-[44px] px-4 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              )}
              <div className="text-sm text-muted-foreground self-center">
                Showing {filteredLeads.length.toLocaleString()} of {leads.length.toLocaleString()} leads
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <PremiumTable
        data={filteredLeads}
        columns={columns}
        onRowClick={handleViewLead}
        emptyMessage="No leads found. Try adjusting search or filters."
      />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Management Tools</CardTitle>
          <CardDescription>
            Quick access to common lead management tasks and workflows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Desktop grid */}
          <div className="hidden sm:grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-info/10 text-info flex-shrink-0">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Follow Up</div>
                  <div className="text-sm text-muted-foreground">Contact pending leads</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-success/10 text-success flex-shrink-0">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Sales Report</div>
                  <div className="text-sm text-muted-foreground">View conversion metrics</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                  <Calendar className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Schedule Calls</div>
                  <div className="text-sm text-muted-foreground">Book appointments</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-accent/10 text-accent flex-shrink-0">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Import Leads</div>
                  <div className="text-sm text-muted-foreground">Bulk import from CSV</div>
                </div>
              </div>
            </Button>
          </div>
          {/* Mobile horizontal scroll */}
          <div className="sm:hidden">
            <ScrollArea className="w-full">
              <div className="flex gap-3 pb-2">
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-info/10 text-info mr-3">
                    <MessageCircle className="h-4 w-4" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="text-sm font-medium leading-tight">Follow Up</div>
                    <div className="text-xs text-muted-foreground">Contact pending leads</div>
                  </div>
                </Button>
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-success/10 text-success mr-3">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="text-sm font-medium leading-tight">Sales Report</div>
                    <div className="text-xs text-muted-foreground">View conversion metrics</div>
                  </div>
                </Button>
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary mr-3">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="text-sm font-medium leading-tight">Schedule Calls</div>
                    <div className="text-xs text-muted-foreground">Book appointments</div>
                  </div>
                </Button>
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-accent/10 text-accent mr-3">
                    <Plus className="h-4 w-4" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="text-sm font-medium leading-tight">Import Leads</div>
                    <div className="text-xs text-muted-foreground">Bulk import from CSV</div>
                  </div>
                </Button>
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </CardContent>
      </Card>

      {/* View Lead Dialog - Redesigned */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 shadow-2xl">
          {selectedLead && (
            <>
              {/* Header with gradient accent */}
              <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-5">
                <button
                  onClick={() => setIsViewDialogOpen(false)}
                  className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-white truncate">
                      {selectedLead.client_name}
                    </h2>
                    <p className="text-sm text-slate-300 mt-0.5">
                      Lead #{selectedLead.id.toString().slice(-6).toUpperCase()}
                    </p>
                  </div>
                    <Badge className={`${
                      selectedLead.status === 'new' ? 'bg-info/10 text-info border-info/20' :
                      selectedLead.status === 'contacted' ? 'bg-warning/10 text-warning border-warning/20' :
                      selectedLead.status === 'qualified' ? 'bg-primary/10 text-primary border-primary/20' :
                      selectedLead.status === 'quoted' ? 'bg-accent/10 text-accent border-accent/20' :
                      selectedLead.status === 'won' ? 'bg-success/10 text-success border-success/20' :
                      selectedLead.status === 'lost' ? 'bg-destructive/10 text-destructive border-destructive/20' :
                      'bg-muted text-muted-foreground'
                    } border font-medium px-3 py-1`}>
                    {selectedLead.status}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-6 max-h-[60vh] overflow-y-auto">
                {/* Contact Information */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Contact Information
                  </h3>
                  <div className="space-y-2">
                    {/* Email */}
                    <div className="group flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-info/100/10 flex items-center justify-center">
                        <Mail className="h-4 w-4 text-info" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm font-medium truncate">{selectedLead.client_email}</p>
                      </div>
                    </div>

                    {/* Phone */}
                    {selectedLead.client_phone && (
                      <div className="group flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-success/100/10 flex items-center justify-center">
                          <Phone className="h-4 w-4 text-success" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground">Phone</p>
                          <p className="text-sm font-medium">{formatPhone(selectedLead.client_phone)}</p>
                        </div>
                      </div>
                    )}

                    {/* Address */}
                    {selectedLead.service_street && (
                      <div className="group flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-accent/100/10 flex items-center justify-center">
                          <MapPin className="h-4 w-4 text-accent" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground">Service Address</p>
                          <p className="text-sm font-medium">{selectedLead.service_street}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Service Request */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Service Request
                  </h3>
                  {selectedLead.service_requested && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 mb-3">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/100/10 flex items-center justify-center">
                        <Wrench className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Service Type</p>
                        <p className="text-sm font-medium">{selectedLead.service_requested}</p>
                      </div>
                    </div>
                  )}

                  {selectedLead.message && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {parsedMessage?.reference && (
                          <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-2">
                            <MessageCircle className="h-4 w-4 text-muted-foreground" />
                            <div className="text-sm">
                              <p className="text-xs text-muted-foreground uppercase">Reference</p>
                              <p className="font-medium">{parsedMessage.reference}</p>
                            </div>
                          </div>
                        )}
                        {(parsedMessage?.appointment?.date || parsedMessage?.appointment?.time || parsedMessage?.appointment?.window) && (
                          <div className="p-3 rounded-lg bg-muted/30">
                            <p className="text-xs text-muted-foreground uppercase mb-1">Appointment</p>
                            <div className="space-y-1 text-sm">
                              {parsedMessage.appointment.date && <div>Date: {parsedMessage.appointment.date}</div>}
                              {parsedMessage.appointment.time && <div>Time: {parsedMessage.appointment.time}</div>}
                              {parsedMessage.appointment.window && <div>Window: {parsedMessage.appointment.window}</div>}
                            </div>
                          </div>
                        )}
                        {parsedMessage?.location && (
                          <div className="p-3 rounded-lg bg-muted/30">
                            <p className="text-xs text-muted-foreground uppercase mb-1">Location</p>
                            <p className="text-sm font-medium leading-tight">{parsedMessage.location}</p>
                          </div>
                        )}
                        {parsedMessage?.availability && (
                          <div className="p-3 rounded-lg bg-muted/30">
                            <p className="text-xs text-muted-foreground uppercase mb-1">Availability</p>
                            <p className="text-sm leading-tight">{parsedMessage.availability}</p>
                          </div>
                        )}
                        {parsedMessage?.service && (
                          <div className="p-3 rounded-lg bg-muted/30">
                            <p className="text-xs text-muted-foreground uppercase mb-1">Service</p>
                            <p className="text-sm leading-tight">{parsedMessage.service}</p>
                          </div>
                        )}
                        {parsedMessage?.extras?.map(extra => (
                          <div key={`${extra.label}-${extra.value}`} className="p-3 rounded-lg bg-muted/30">
                            <p className="text-xs text-muted-foreground uppercase mb-1">{extra.label}</p>
                            <p className="text-sm leading-tight">{extra.value}</p>
                          </div>
                        ))}
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          <p className="text-xs font-medium text-muted-foreground">Customer Message</p>
                        </div>
                        <div className="relative">
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
                          <p className="text-sm leading-relaxed pl-4 py-2 text-muted-foreground whitespace-pre-wrap">
                            {parsedMessage?.notes?.length ? parsedMessage.notes.join('\n') : selectedLead.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                {/* Submission Details */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Submission Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Form Source</p>
                      </div>
                      <p className="text-sm font-medium">{selectedLead.form_name || selectedLead.form_id || 'Contact Form'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Submitted</p>
                      </div>
                      {(() => {
                        const submitted = formatRelativeDate(selectedLead.created_at, { includeTime: true })
                        return (
                          <p className="text-sm font-medium leading-tight">
                            {submitted.relative && <span className="mr-1 text-foreground">{submitted.relative}</span>}
                            <span className="text-muted-foreground">{submitted.absolute}</span>
                          </p>
                        )
                      })()}
                    </div>
                  </div>
                  {selectedLead.submission_url && (
                    <Button
                      variant="secondary"
                      size="sm"
                      asChild
                      className="mt-3 gap-2"
                    >
                      <a
                        href={selectedLead.submission_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View submission page
                      </a>
                    </Button>
                  )}
                </section>

                {/* Files */}
                {selectedLead.file_uploads && Array.isArray(selectedLead.file_uploads) && selectedLead.file_uploads.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Uploaded Files
                    </h3>
                    <div className="space-y-2">
                      {selectedLead.file_uploads.map((file: any, index: number) => (
                        <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-500/10 flex items-center justify-center">
                            <FileText className="h-4 w-4 text-slate-500" />
                          </div>
                          <span className="flex-1 text-sm font-medium">{file.name || `File ${index + 1}`}</span>
                          {file.url && (
                            <Button size="sm" variant="ghost" asChild className="h-8">
                              <a href={file.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Footer Actions */}
              <div className="px-6 py-4 bg-muted/30 border-t flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `mailto:${selectedLead.client_email}`}
                    className="gap-2 hover:bg-info/10 hover:text-info hover:border-info dark:hover:bg-info/10 dark:hover:text-info dark:hover:border-info"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                  {selectedLead.client_phone && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `tel:${selectedLead.client_phone}`}
                      className="gap-2 hover:bg-success/10 hover:text-success hover:border-success dark:hover:bg-success/20 dark:hover:text-success dark:hover:border-success"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </Button>
                  )}
                </div>
                <Select value={selectedLead.status} onValueChange={(value) => handleStatusChange(selectedLead.id, value as Lead['status'])}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="quoted">Quoted</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
